import _ from "lodash";
import { defineStore } from "pinia";
import dayjs from "dayjs";
import { JobAction, JobItemStatus } from "~~/interface/Job";
import { nanoid } from "nanoid";
import { SETTING_ID } from "~~/interface/Setting";

// MODAL POS PAYMENT

interface PaymentOption {
  paymentId: string;
  name: string;
  isEntertain?: boolean;
}

interface Payment {
  amount: string | number;
  item: PaymentOption;
  list: PaymentOption[];
}

interface PaymentState {
  active: boolean;
  grandTotal: number;
  table: string;
  note: string;
  activeIndex: number;
  readyToSubmit: boolean;
  options: PaymentOption[];
  payments: Payment[];
  hash: string;
  roomList: any[];
  roomEnable: boolean;
  isRoomCharge: boolean;
  room: any;
  isPaid: boolean;
  isLoading: boolean;
  tags: string[];  
}

function handleRoomCharge(isRoomCharge: boolean, grandTotal: number) {
  if (isRoomCharge) {
    return [
      {
        paymentId: "roomCharge",
        name: "Room Charge",
        amount: grandTotal,
      },
    ];
  }
  return null;
}

export const usePaymentStore = defineStore("payment", {
  state: (): PaymentState => ({
    active: false,
    grandTotal: 0,
    table: "A1",
    note: "",
    activeIndex: 0,
    readyToSubmit: false,
    options: _generatePaymentOptions(),
    payments: [],
    hash: nanoid(6),
    roomList: _genRoomList(),
    roomEnable: false,
    isRoomCharge: false,
    room: null,
    isPaid: false,
    isLoading: false,
    tags: [],
    isEntertain:false
  }),
  getters: {
    remainingAmount(state) {
      if(this.isEntertain) { 
        return 0;
      }
      this.readyToSubmit = false; 
      return (
        this.getGrandTotal -
        state.payments.reduce((a, b) => a + parseFloat(b.amount || 0), 0)
      );
    },
    isEntertain(state) {
      return state.payments.filter((p) => p.item.isEntertain).length > 0;
    },
    getMoney() {
      let h = this.hash; // force recompute

      const posStore = usePosStore();
      const jobsStore = useJobsStore();
      const settingsStore = useSettingsStore();
      const roundingType = settingsStore.getRoundingType;

      let bill = posStore.billFilter || "all";
      let itemsCombined = jobsStore.itemsCombined(posStore.selectedTable, {
        bill,
      });

      let res = utilsItemComputeOneTable({
        items: itemsCombined,
        roundingType,
      });

      return res;
    },

    getGrandTotal(state) {
      let h = this.hash; // force recompute

      if(this.isEntertain) { 
        return 0;
      }

      const posStore = usePosStore();
      const jobsStore = useJobsStore();
      const settingsStore = useSettingsStore();
      const roundingType = settingsStore.getRoundingType;

      let bill = posStore.billFilter || "all";
      let itemsCombined = jobsStore.itemsCombined(posStore.selectedTable, {
        bill,
      });

      let res = utilsItemComputeOneTable({
        items: itemsCombined,
        roundingType,
      });

      return res.grandTotal;
    },
    getItemLength(state) {
      const posStore = usePosStore();
      const jobsStore = useJobsStore();

      let bill = posStore.billFilter || "all";
      let itemsCombined = jobsStore.itemsCombined(posStore.selectedTable, {
        bill,
      });

      return itemsCombined.length;
    },
  },
  actions: {
    addPayment() {
      this.payments.push({
        amount: "",
        item: this.options[0],
        list: this.options,
      });
    },
    payEqual(grandTotal) {
      if (this.readyToSubmit) {
        this.submit();
      } else {
        const paymentAmount = grandTotal / this.payments.length;
        this.payments.forEach((payment) => {
          payment.amount = this.isEntertain ? 0 : paymentAmount;
        });

        setTimeout(() => {
          this.readyToSubmit = true;
        }, 10);
      }
    },
    removePayment(index) {
      if (index == 0) {
        return;
      }

      let p = this.payments[index];
      this.payments[this.payments.length - 1].list.push(p.item);

      this.payments.splice(index, 1);
      this.activeIndex = this.payments.length - 1; // Set Lastest Index For Input
    },
    changeTag(tag) {
      this.tags = tag;
    },
    init(computedGrandTotal) {
      let settingPaymentList = useSettingsStore().getValueBysettingId(
        SETTING_ID.PAYMENT,
        _generatePaymentOptions()
      );

      const cloneSettingPaymentList = _.cloneDeep(settingPaymentList);
      this.options = cloneSettingPaymentList;

      this.payments = [
        {
          amount: "",
          item: this.options[0],
          list: this.options,
        },
      ];
      this.isLoading = false;
      this.active = true;
      this.table = usePosStore().selectedTable;
      this.hash = nanoid(6);
      this.roomList = useSettingsStore().getRooms;
      this.isRoomCharge = false;
      this.room = null;
      this.isPaid = false;
      this.roomEnable = false;
      if (this.roomList?.length >= 1) {
        this.roomEnable = true;
      }
    },
    async submit() {      
      if (this.isPaid) return;

      // ด่านสุดท้าย: เครื่องถือ OPEN ของรอบที่ปิดไปแล้ว ใบเสร็จจะไปลงวันเก่า
      // ต้องกันก่อนสร้างใบเสร็จ/PAY job (หน้าจ่ายเงินอาจเปิดค้างไว้ก่อน doc รอบใหม่มาถึง)
      if (useSessionStale().blockIfStale()) return;

      this.isPaid = true;
      this.hash = nanoid(6);
      //--- entertain payment
      if (this.isEntertain) {
        let entertainMode = "1";
        if (entertainMode == "1") {
          this.setEntertainItemsDiscount();
        } else if (entertainMode == "2") {
          this.setEntertainDiscountBill();
        }
        this.isLoading = true;

        await useDelaySecs(1);
      }
      // --

      let paymentTransformed = this.processPayments(
        this.payments,
        this.getGrandTotal
      );

      

      let roomCharge = handleRoomCharge(
        this.isRoomCharge,
        this.getGrandTotal
      );
      if (roomCharge) {
        paymentTransformed = roomCharge;
      }

      // เครื่องเพิ่งเปิด/เพิ่งข้ามเดือน ต้อง seed เลขใบเสร็จจาก cloud ให้เสร็จก่อน (รอไม่เกิน 5 วิ)
      // ไม่งั้นบิลแรกจะได้ -0001 ซ้ำกับของเดิม
      await useReceiptIdCounterStore().ready(
        dayjs(useJobsStore().openShop?.businessDate).format("YYYY-MM-DD")
      );

      // สร้างให้ครบทั้งใบเสร็จและ PAY job ก่อน แล้วค่อยยิงลง database
      //
      // ของเดิมบันทึกใบเสร็จไปก่อนแล้วค่อยสร้าง job ถ้า _generateJob พังตรงนั้น
      // (JobBuilder.build() -> useServerTime() เคย throw จริง) จะเหลือใบเสร็จที่ไม่มี PAY job คู่กัน
      // ของบนโต๊ะไม่ถูกตัดเป็น PAID โต๊ะเลยไม่เคลียร์ สลิปก็ไม่ออก แคชเชียร์เห็นว่ายังไม่จ่าย
      // เลยกดซ้ำ กลายเป็นเก็บเงินลูกค้าหลายรอบจากบิลใบเดียว
      //
      // ย้ายการสร้างมาไว้ก่อนทั้งคู่ ถ้าพังจะพังตั้งแต่ยังไม่มีอะไรถูกบันทึก = ปลอดภัย ไม่มีบิลซ้ำ
      let receipt = this._generateReceipt(paymentTransformed);
      let jobPayment = this._generateJob(paymentTransformed);

      // ลำดับการเขียน database ยังต้องเป็นใบเสร็จก่อนแล้วค่อย jobchain
      useReceiptsStore().add(receipt, true);
      useJobsStore().add(jobPayment, true);

      // useToastStore().show({
      //   title: "Payment Success",
      //   message: `${t('pos.paymentModal.label.change')} ${Math.abs(this.remainingAmount?.toFixed(2) || 0)}`,
      //   type: "success",
      //   timer: 30000,
      // });

      this.clearAfterPayment(jobPayment);
      this._printReceipts(receipt);

      return receipt;
    },
    handleNumbpad(value) {
      let _a = this.payments[this.activeIndex].amount;
      if (value === "back") {
        _a = _a.toString().slice(0, -1);
      } else if (value === "clear") {
        _a = "";
      } else {
        _a = _a.toString() + value;
      }
      this.payments[this.activeIndex].amount = _a;
    },
    processPayments(payments: Payment[], total: number) {
      const posStore = usePosStore();
      const jobsStore = useJobsStore();
      let bill = posStore.billFilter;
      let itemsCombined = jobsStore.itemsCombined(posStore.selectedTable, {
        bill,
      });

      const paymentWithoutCash = payments.filter((f) => f.item.name !== "Cash");
      const paymentCash = payments.filter((f) => f.item.name === "Cash");

      const resultPaymentWithoutCash = _computePaymentTransformed(
        paymentWithoutCash,
        total,
        itemsCombined
      );
      total = resultPaymentWithoutCash.total;

      const resultPaymentCash = _computePaymentTransformed(
        paymentCash,
        total,
        itemsCombined
      );
      total = resultPaymentCash.total;

      return [
        ...resultPaymentWithoutCash.transform,
        ...resultPaymentCash.transform,
      ];
    },

    _generateReceipt(paymentTransformed) {
      const jobsStore = useJobsStore();
      const posStore = usePosStore();
      const shopStore = useShopStore();
      const employeePinStore = useEmployeePinStore();
      const settingsStore = useSettingsStore();
      let businessDate = jobsStore.openShop.businessDate;
      const roundingType = settingsStore.getRoundingType;

      let bill = posStore.billFilter;
      let itemsCombined = jobsStore.itemsCombined(posStore.selectedTable, {
        bill,
      });

      // เลขจากตัวนับรายเดือน (localStorage + Firestore) ดู stores/receiptIdCounter.ts
      // nonce = hash ของบิล ใช้กันนับซ้ำตอน sync ขึ้น Firestore
      const _NextReceiptId = (nonce: string) => {
        const yyyymm = dayjs(businessDate).format("YYYYMM");
        const n = useReceiptIdCounterStore().getNextReceiptIdAndIncrement(nonce, yyyymm);
        return `${yyyymm}-${n.toString().padStart(4, "0")}`;
      };

      let moneySummary = jobsStore.computeMoneyOneTable(
        posStore.selectedTable,
        roundingType
      );

      let sumAmountPayment =
        _.sumBy(paymentTransformed, "amountInput") || this.getGrandTotal;

      let _payment = {
        paymentList: paymentTransformed,
        received: sumAmountPayment,
        change: sumAmountPayment - this.getGrandTotal,
        summary: moneySummary,
        note: this.note || "",
        isEntertain: paymentTransformed.some((p) => p.isEntertain || false),
      };

      const currentTable = posStore.selectedTable;
      const guest = jobsStore.tables[currentTable]?.guests || 1;
      const channel = posStore.tableState[currentTable]?.channel || {
        channelId: "default",
        name: "Default",
      };

      _payment.summary.grandTotal = this.getGrandTotal;
      _payment.summary.subTotal = __sumChain(itemsCombined, "extendedPrice");
      _payment.summary.dc1 = __sumChain(itemsCombined, "dc1");
      _payment.summary.dc2 = __sumChain(itemsCombined, "dc2");
      _payment.summary.tax = __sumChain(itemsCombined, "tax");
      _payment.summary.sc = __sumChain(itemsCombined, "sc");
      _payment.summary.cc = __sumChain(itemsCombined, "cc");
      // อัตรา card charge ของบิลนี้ ([mode, value]) ไว้พิมพ์ป้าย "Card Charge x%"
      // ใบเสร็จพิมพ์หลังโต๊ะถูก CLEAR ไปแล้ว อ่านจาก tableState ตอนพิมพ์จะได้ 0%
      // ค่าเดียวกับที่ TablesBuilder ใช้คิดเงิน
      _payment.summary.ccSetting =
        jobsStore.tablesInfo[currentTable]?.cc || (jobsStore.settings as any)?.cc || [0, 0];


      const currentShift = useShiftWork(jobsStore.jobs).getCurrentShiftWorkNumber()     
      const categoriesSummary = useCategoriesSummary().buildCategorySummary(itemsCombined,  { 
        rounding : _payment.summary.rounding || 0
      })

      // เปิดเลขสุ่มอยู่ไม่ต้องกินเลข running
      const _receiptId = settingsStore.getValueBysettingId(SETTING_ID.PRINTER_RECEIPT_ID_RANDOM, false)
        ? this.hash
        : _NextReceiptId(this.hash)

      const computerUuid = useLocalStorage("computerUuid", nanoid(6));

      let receipt = new ReceiptBuilder()
        .withTable(posStore.selectedTable)
        .withItems(itemsCombined)
        .withReceiptId(_receiptId)
        .withBusinessDate(businessDate)
        .withEmployee(employeePinStore.employee)
        .withPayment(_payment)
        .withMetadata({ room: this.room, tags: this.tags, shift: currentShift, computerId :computerUuid.value || null })
        .withStoreId(shopStore.storeId)
        .withBill(bill == "all" ? undefined : bill)
        .withGuests(guest)
        .withChannel(channel)
        .withCategoriesSummary(categoriesSummary)
        .withCustom({
          hash: this.hash,
        
        })
        .build();

      return receipt;
    },
    _generateJob(paymentTransformed) {
      const jobsStore = useJobsStore();
      const posStore = usePosStore();
      const employeePinStore = useEmployeePinStore();

      let bill = posStore.billFilter;
      let items = jobsStore.tables[posStore.selectedTable].items;

      if (bill !== "all") {
        items = _.filter(items, (v, k) => {
          return v.bill === bill;
        });
      }

      items = _.map(items, (v, k) => {
        return {
          uuid: v.uuid,
          productId: v.productId,
          qty: -v.qty,
        };
      });

      let job = new JobBuilder()
        .withTable(posStore.selectedTable)
        .withItems(items)
        .withEmployee(employeePinStore.employee)
        .withAction(JobAction.PAY)
        .withPayment(paymentTransformed)
        .withCustom({
          hash: this.hash,
        })
        .build();

      return job;
    },
    // ลิ้นชักจะเด้งเมื่อช่องทางชำระใน settings ติ๊ก openDrawer ไว้
    // ค่าที่ไม่มี (ข้อมูลเดิมที่ตั้งไว้ก่อนมีฟีเจอร์นี้) หรือ paymentId ที่ไม่อยู่ในลิสต์
    // (เช่น roomCharge) ถือว่าเด้ง เพื่อคงพฤติกรรมเดิม
    _shouldOpenDrawer(receipt: any): boolean {
      const options = useSettingsStore().getValueBysettingId(
        SETTING_ID.PAYMENT,
        []
      );
      const list = receipt?.payment?.list || [];
      if (!list.length) return true;

      return list.some((p: any) => {
        const option = _.find(options, { paymentId: p.paymentId });
        return option?.openDrawer !== false;
      });
    },
    _printReceipts(receipt: any) {
      const shopStore = useShopStore();
      let shopName = shopStore?.name || "Shop Name"
      const shouldOpenDrawer = this._shouldOpenDrawer(receipt);

      setTimeout(async () => {
        if (shouldOpenDrawer) {
          new ThermalPrinterReceipt().openDrawer();
        }
        const printReceipt = await new ThermalPrinterReceipt().setReceipt(
          receipt,
          {
            shopName,
          }
        );
        printReceipt.print();
      }, 5);

      setTimeout(async () => {
        const settingsStore = useSettingsStore();
        const isCopyReceipt = settingsStore.getValueBysettingId(
          SETTING_ID.PRINTER_RECEIPT_COPY,
          0
        );
        if (isCopyReceipt) {
          const printReceipt = await new ThermalPrinterReceipt().setReceipt(
            receipt,
            { shopName, isCopy: true }
          );
          printReceipt.print();
        }

        usePosStore().selectBill("all");
      }, 100);
    },
    setEntertainItemsDiscount() {
      let mode = 0;
      let value = 100;
      const jobsStore = useJobsStore();
      const posStore = usePosStore();
      const employeePinStore = useEmployeePinStore();
      let items = jobsStore.getUuidItemsByTable(
        this.table,
        JobItemStatus.ACTIVE
      );
       
      let bill = posStore.billFilter;
      if(bill !== "all") { 
        items =  items.filter((v, k) => {
          return v.bill === bill;
        });
      }
      items = _.map(items, (v, k) => {
        return {
          uuid: v.uuid,
          productId: v.productId,
          qty: v.qty,
          discountItem: [mode, value],
        };
      });

      // for summary
      let job = new JobBuilder()
        .withTable(this.table)
        .withItems(items)
        .withEmployee(employeePinStore.employee)
        .withAction(JobAction.SET_DISCOUNT_ENT)
        .withJobIdAndTimestamp(
          useServerTime().getCorrectedDayjsInstance().toISOString()
        )
        .build();

      jobsStore.add(job, true);
    },
    // จ่ายจนโต๊ะไม่เหลือรายการ = โต๊ะว่าง ล้างสถานะทั้งหมดด้วย CLEAR event เดียว
    // (ล็อค / แขก / หมายเหตุ / channel / ส่วนลดท้ายบิล ฯลฯ + ลบ event เก่าของโต๊ะออกจาก RTDB)
    // จ่ายบิลย่อยแล้วยังเหลือบิลอื่น = แค่ปลดล็อค ไม่งั้นบิลที่เหลือเสีย channel / ส่วนลดไปด้วย
    clearAfterPayment(paidJob?: any) {
      const posStore = usePosStore();
      const jobsStore = useJobsStore();

      // uuidItems ยังไม่ถูก recompute ตอนนี้ (debounce) จึงเช็คกับ uuid ที่เพิ่งจ่ายเอง เหมือน pos.void()
      const paidUuids = new Set(_.map(paidJob?.items, "uuid"));
      const remaining = _.filter(
        jobsStore.getUuidItemsByTable(this.table, JobItemStatus.ACTIVE),
        (item: any) => !paidUuids.has(item.uuid)
      );

      if (remaining.length == 0) {
        useTableStatesEventsStore().clearTable(this.table, { reason: "PAY", ref: paidJob?.jobId });
      } else {
        posStore.unlockTable(this.table);
      }

      this.active = false;
      this.tags = [];
    },

    setEntertainDiscountBill() {
      const jobsStore = useJobsStore();
      const posStore = usePosStore();

      const payload = [0, 100];
      jobsStore.setOptions(posStore.selectedTable, "dc2", payload);
    },

    genPaymentOptions(){
      return _generatePaymentOptions()
    }
  },
});

function _genRoomList() {
  return [
    {
      label: "Room #101",
      value: 101,
    },
    {
      label: "Room #102",
      value: 102,
    },
    {
      label: "Room #103",
      value: 103,
    },
    {
      label: "South #S101",
      value: "S101",
    },
  ];
}

function _generatePaymentOptions() {
  return [
    {
      paymentId: "cash",
      name: "Cash",
      openDrawer: true,
    },
    {
      paymentId: "creditcard",
      name: "Credit Card",
      openDrawer: true,
    },
    {
      paymentId: "transfered",
      name: "โอน",
      openDrawer: true,
    },
    {
      paymentId: "pending",
      name: "รอโอน",
      openDrawer: true,
    },
  ];
}

function _computePaymentTransformed(payments, total, items) {
  let transform = [];
  _.forEach(payments, (payment) => {
    let computedAmount =
      parseFloat(payment.amount) >= total ? total : parseFloat(payment.amount || 0);
    transform.push({
      paymentId: payment.item.paymentId,
      name: payment.item.name,
      amount: computedAmount,
      amountInput: Number(payment.amount),

      //--
      isEntertain: payment.item.isEntertain || false,
      entertainMode: payment.item.entertainMode || null,
      entertainAmount: payment.item.isEntertain
        ? sumEntertainItems(items, payment.item.entertainMode)
        : 0,
    });
    total -= computedAmount;
  });
  return { transform, total };
}

function sumEntertainItems(items, mode = "dc1") {
  return _.sumBy(items, (item) => (mode == "dc1" ? item.dc1[2] : item.dc2[2]));
}
